/**
 * Execution.tsx — /app/execution  (v8.8)
 *
 * Execution Intelligence workspace.
 * Transforms intelligence into structured trade plans.
 *
 * Sub-tabs: PLAN | SETUPS | SCENARIOS | RISK
 */
import React, { useState, useMemo } from 'react';
import { useStore }           from '../../store';
import { WatchlistSidebar }   from '../../features/watchlist/WatchlistSidebar';
import { getSymbolContext }   from '../../selectors/symbolContext';
import { getThesis }          from '../../ai/thesisEngine';
import {
  buildExecutionPlan,
  TradePlan, SetupCandidate, PriceTarget, Scenario,
} from '../../domains/execution/executionIntelligence';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  gold:   '#C9A84C', border: '#1A3050', card: 'rgba(13,31,53,0.55)',
  text:   '#E8EDF5', dim:   '#4A6FA5', muted: '#3A5070',
  green:  '#4ADE80', red:   '#F87171', amber: '#FCD34D',
  blue:   '#38BDF8', mono:  "'Courier New', monospace",
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function dirColor(dir: string) {
  return dir === 'long' ? C.green : C.red;
}
function qualColor(tier: string) {
  return tier === 'high' ? C.green : tier === 'moderate' ? C.amber : C.muted;
}
function setupIcon(type: string) {
  const m: Record<string, string> = {
    continuation: '→', reversal: '↩', trap_reversal: '⚠', breakout: '▶', breakout_fade: '◀',
  };
  return m[type] ?? '·';
}

// ── Plan tab ──────────────────────────────────────────────────────────────────

function PlanTab({ plan }: { plan: TradePlan }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      {/* Summary header */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: dirColor(plan.direction), textTransform: 'uppercase' }}>
            {plan.direction}
          </span>
          <span style={{ fontSize: 11, color: C.text }}>{plan.setupType.replace('_', ' ')}</span>
          <span style={{ fontSize: 9, letterSpacing: 1, color: qualColor(plan.quality.tier),
            border: `1px solid ${qualColor(plan.quality.tier)}40`, borderRadius: 2, padding: '1px 5px', textTransform: 'uppercase' }}>
            {plan.quality.tier} quality
          </span>
          <span style={{ fontSize: 10, color: C.muted, fontFamily: C.mono, marginLeft: 'auto' }}>
            {(plan.quality.overall * 100).toFixed(0)}%
          </span>
        </div>
        <div style={{ fontSize: 10, color: '#5A7FA8', lineHeight: 1.6 }}>{plan.summary}</div>
      </div>

      {/* Entry zone */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>ENTRY ZONE</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>TYPE</div>
            <div style={{ fontSize: 12, color: C.gold, fontWeight: 700, textTransform: 'uppercase' }}>
              {plan.entryZone.type.replace('_', ' ')}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>IDEAL ENTRY</div>
            <div style={{ fontSize: 16, color: C.text, fontWeight: 700, fontFamily: C.mono }}>
              {plan.entryZone.idealEntry.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>ZONE</div>
            <div style={{ fontSize: 12, color: C.dim, fontFamily: C.mono }}>
              {plan.entryZone.zoneBottom.toFixed(2)} – {plan.entryZone.zoneTop.toFixed(2)}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#5A7FA8' }}>
          <span style={{ color: C.muted }}>Condition: </span>{plan.entryZone.condition}
        </div>
      </div>

      {/* Risk */}
      <div style={{ padding: '14px', background: 'rgba(248,113,113,0.05)', border: `1px solid rgba(248,113,113,0.2)`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.red, marginBottom: 10 }}>RISK / STOP</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>STOP PRICE</div>
            <div style={{ fontSize: 16, color: C.red, fontWeight: 700, fontFamily: C.mono }}>
              {plan.risk.stopPrice.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>DISTANCE</div>
            <div style={{ fontSize: 12, color: C.dim, fontFamily: C.mono }}>
              {plan.risk.stopPct.toFixed(1)}% / ${plan.risk.stopDistance.toFixed(2)}/share
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#5A7FA8' }}>
          <span style={{ color: C.muted }}>Basis: </span>{plan.risk.structuralBasis}
        </div>
        <div style={{ fontSize: 10, color: C.red, marginTop: 4 }}>
          <span style={{ color: C.muted }}>Invalidation: </span>{plan.risk.invalidationEvent}
        </div>
      </div>

      {/* Targets */}
      {plan.targets.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>TARGETS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {plan.targets.map((tgt: PriceTarget, i: number) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 11 }}>
                <span style={{ color: C.muted, minWidth: 20 }}>T{i + 1}</span>
                <span style={{ color: C.green, fontWeight: 700, fontFamily: C.mono, minWidth: 60 }}>
                  {tgt.price.toFixed(2)}
                </span>
                <span style={{ color: C.amber, fontFamily: C.mono, minWidth: 40 }}>{tgt.rMultiple}R</span>
                <span style={{ color: '#5A7FA8', fontSize: 9, textTransform: 'uppercase' }}>{tgt.basis}</span>
                <span style={{ color: C.muted, flex: 1, fontSize: 10 }}>{tgt.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quality breakdown */}
      <div style={{ padding: '12px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>QUALITY BREAKDOWN</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 8 }}>
          {[
            { label: 'STRUCTURE',   v: plan.quality.structureScore },
            { label: 'CONFLUENCE',  v: plan.quality.confluenceScore },
            { label: 'VOL FIT',     v: plan.quality.volatilityFit },
            { label: 'EDGE ALIGN',  v: plan.quality.edgeAlignment },
          ].map(m => (
            <div key={m.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 7, letterSpacing: 1, color: C.muted, marginBottom: 3 }}>{m.label}</div>
              <div style={{ height: 3, background: '#1A3050', borderRadius: 2, marginBottom: 3 }}>
                <div style={{ width: `${m.v * 100}%`, height: '100%', background: m.v >= 0.7 ? C.green : m.v >= 0.5 ? C.amber : C.red, borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 10, color: C.text, fontFamily: C.mono }}>{(m.v * 100).toFixed(0)}%</div>
            </div>
          ))}
        </div>
        {plan.quality.notes.slice(0, 3).map((n, i) => (
          <div key={i} style={{ fontSize: 9, color: C.muted, lineHeight: 1.6 }}>› {n}</div>
        ))}
      </div>
    </div>
  );
}

// ── Setups tab ────────────────────────────────────────────────────────────────

function SetupsTab({ candidates }: { candidates: SetupCandidate[] }) {
  if (candidates.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No setup conditions detected. Await structural confirmation.
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 760 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 4 }}>
        {candidates.length} SETUP CANDIDATE{candidates.length > 1 ? 'S' : ''} DETECTED
      </div>
      {candidates.map((c: SetupCandidate, i: number) => (
        <div key={i} style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 16, color: dirColor(c.direction) }}>{setupIcon(c.type)}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text, textTransform: 'uppercase' }}>
              {c.type.replace(/_/g, ' ')}
            </span>
            <span style={{ color: dirColor(c.direction), fontSize: 10, fontWeight: 700 }}>{c.direction.toUpperCase()}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 9, color: C.muted }}>EVIDENCE</span>
              <span style={{ fontSize: 12, color: c.evidence >= 70 ? C.green : c.evidence >= 50 ? C.amber : C.muted, fontFamily: C.mono, fontWeight: 700 }}>
                {c.evidence}%
              </span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#5A7FA8', marginBottom: 6 }}>{c.rationale}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {c.rules.map((r, j) => (
              <span key={j} style={{ fontSize: 8, color: C.dim, border: `1px solid ${C.border}`, borderRadius: 2, padding: '1px 5px', fontFamily: C.mono }}>
                {r}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Scenarios tab ─────────────────────────────────────────────────────────────

function ScenariosTab({ plan }: { plan: TradePlan }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 4 }}>
        CONDITIONAL SCENARIO MODEL — {plan.scenarios.length} PATHS
      </div>
      {plan.scenarios.map((sc: Scenario, i: number) => {
        const weight = sc.weight;
        const weightColor = weight >= 0.45 ? C.green : weight >= 0.2 ? C.amber : C.muted;
        return (
          <div key={i} style={{ padding: '12px 14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 9, color: C.muted, minWidth: 20, paddingTop: 1 }}>IF</span>
              <span style={{ fontSize: 11, color: C.text, flex: 1 }}>{sc.trigger}</span>
              <span style={{ fontSize: 10, color: weightColor, fontFamily: C.mono, minWidth: 40, textAlign: 'right', fontWeight: 700 }}>
                {(weight * 100).toFixed(0)}%
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 9, color: C.dim, minWidth: 20, paddingTop: 1 }}>→</span>
              <span style={{ fontSize: 10, color: '#5A7FA8', flex: 1 }}>{sc.expectation}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 9, color: C.gold, minWidth: 20, paddingTop: 1 }}>DO</span>
              <span style={{ fontSize: 10, color: C.text, flex: 1 }}>{sc.response}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Risk tab ─────────────────────────────────────────────────────────────────

function RiskTab({ plan }: { plan: TradePlan }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760 }}>
      {/* Stop level */}
      <div style={{ padding: '14px', background: 'rgba(248,113,113,0.05)',
        border: `1px solid rgba(248,113,113,0.25)`, borderLeft: '3px solid #F87171', borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: '#F87171', marginBottom: 10 }}>STOP / INVALIDATION</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>STOP PRICE</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#F87171', fontFamily: C.mono }}>
              {plan.risk.stopPrice.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>DISTANCE</div>
            <div style={{ fontSize: 14, color: C.dim, fontFamily: C.mono }}>
              {plan.risk.stopPct.toFixed(1)}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: 9, color: C.muted }}>$/SHARE</div>
            <div style={{ fontSize: 14, color: C.dim, fontFamily: C.mono }}>
              ${plan.risk.stopDistance.toFixed(2)}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 10, color: '#5A7FA8', marginBottom: 4 }}>
          <span style={{ color: C.muted }}>Structural basis: </span>{plan.risk.structuralBasis}
        </div>
        <div style={{ fontSize: 10, color: '#F87171' }}>
          <span style={{ color: C.muted }}>Invalidation event: </span>{plan.risk.invalidationEvent}
        </div>
      </div>

      {/* R:R summary */}
      {plan.targets.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>RISK/REWARD SUMMARY</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {plan.targets.map((t, i) => {
              const barWidth = Math.min(100, t.rMultiple / 4 * 100);
              return (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <span style={{ color: C.muted, minWidth: 20, fontSize: 10 }}>T{i + 1}</span>
                  <span style={{ color: '#4ADE80', fontFamily: C.mono, fontWeight: 700, minWidth: 55 }}>
                    {t.price.toFixed(2)}
                  </span>
                  <div style={{ flex: 1, height: 6, background: '#1A3050', borderRadius: 3, maxWidth: 200 }}>
                    <div style={{ width: `${barWidth}%`, height: '100%', background: '#4ADE80', borderRadius: 3 }} />
                  </div>
                  <span style={{ color: '#FCD34D', fontFamily: C.mono, minWidth: 35, fontSize: 11 }}>
                    {t.rMultiple}R
                  </span>
                  <span style={{ color: '#5A7FA8', fontSize: 9, textTransform: 'uppercase' }}>{t.basis}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Plan quality components related to risk */}
      <div style={{ padding: '12px 14px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>RISK QUALITY FACTORS</div>
        {[
          { label: 'Structure Score',  v: plan.quality.structureScore,  note: 'SMC alignment + gamma anchor quality' },
          { label: 'Volatility Fit',   v: plan.quality.volatilityFit,   note: 'How well setup fits current vol regime' },
          { label: 'Edge Alignment',   v: plan.quality.edgeAlignment,   note: 'R:R quality and target achievability' },
        ].map(m => (
          <div key={m.label} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, fontSize: 10 }}>
            <span style={{ color: C.dim, minWidth: 120 }}>{m.label}</span>
            <div style={{ width: 80, height: 3, background: '#1A3050', borderRadius: 2 }}>
              <div style={{ width: `${m.v * 100}%`, height: '100%', borderRadius: 2,
                background: m.v >= 0.7 ? '#4ADE80' : m.v >= 0.5 ? '#FCD34D' : '#F87171' }} />
            </div>
            <span style={{ color: C.text, fontFamily: C.mono, minWidth: 30 }}>{(m.v * 100).toFixed(0)}%</span>
            <span style={{ color: C.muted, fontSize: 9 }}>{m.note}</span>
          </div>
        ))}
      </div>

      {/* Reasoning */}
      <div style={{ padding: '10px 12px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>PLAN REASONING</div>
        {plan.reasoning.map((r, i) => (
          <div key={i} style={{ fontSize: 9, color: C.muted, lineHeight: 1.7, fontFamily: C.mono }}>› {r}</div>
        ))}
      </div>
    </div>
  );
}

// ── Main workspace ─────────────────────────────────────────────────────────────

type Tab = 'plan' | 'setups' | 'scenarios' | 'risk';

const TABS: { id: Tab; label: string }[] = [
  { id: 'plan',      label: 'Plan'      },
  { id: 'setups',    label: 'Setups'    },
  { id: 'scenarios', label: 'Scenarios' },
  { id: 'risk',      label: 'Risk'      },
];

export function Execution() {
  const { activeSymbol, symbolData, settings, weights } = useStore();
  const [tab, setTab] = useState<Tab>('plan');
  const sym = activeSymbol ?? '';

  const ctx    = sym ? getSymbolContext(sym, symbolData[sym], settings, weights) : null;
  const thesis = sym ? getThesis(sym)?.current ?? null : null;

  const execPlan = useMemo(
    () => ctx ? buildExecutionPlan(ctx, thesis) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sym, ctx?.dataQuality.completeness, ctx?.curPrice, thesis?.inputHash],
  );

  const hasPlan = execPlan?.primaryPlan != null;

  return (
    <div style={{ display: 'flex', minHeight: 0, flex: '1 1 0%', fontFamily: C.mono }}>
      <aside style={{ width: 200, flexShrink: 0, borderRight: `1px solid ${C.border}`, overflowY: 'auto' }}>
        <WatchlistSidebar />
      </aside>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Sub-tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, letterSpacing: 1.5, fontFamily: 'inherit',
              color: tab === t.id ? C.gold : C.muted,
              borderBottom: tab === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
            }}>
              {t.label.toUpperCase()}
            </button>
          ))}
          {/* Status pill */}
          {sym && execPlan && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: 14, gap: 8 }}>
              {hasPlan ? (
                <span style={{ fontSize: 9, color: C.green, border: `1px solid ${C.green}40`, borderRadius: 2, padding: '1px 5px' }}>
                  {execPlan.primaryPlan!.setupType.replace('_', ' ').toUpperCase()}
                  {' '}
                  {execPlan.primaryPlan!.direction.toUpperCase()}
                </span>
              ) : (
                <span style={{ fontSize: 9, color: C.muted }}>no active plan</span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {!sym ? (
            <div style={{ color: C.muted, textAlign: 'center', paddingTop: 60, fontSize: 11, letterSpacing: 2 }}>
              SELECT A SYMBOL
            </div>
          ) : !execPlan ? (
            <div style={{ color: C.muted, textAlign: 'center', paddingTop: 60, fontSize: 11 }}>
              Run analysis to generate execution intelligence.
            </div>
          ) : (
            <>
              {/* No plan explanation */}
              {!hasPlan && execPlan.noplanReason && (
                <div style={{ padding: '10px 14px', background: 'rgba(74,111,165,0.08)', border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 14, fontSize: 11, color: C.dim }}>
                  ○ {execPlan.noplanReason}
                </div>
              )}

              {tab === 'plan' && hasPlan && (
                <PlanTab plan={execPlan.primaryPlan!} />
              )}
              {tab === 'plan' && !hasPlan && (
                <SetupsTab candidates={execPlan.candidates} />
              )}
              {tab === 'setups' && (
                <SetupsTab candidates={execPlan.candidates} />
              )}
              {tab === 'scenarios' && hasPlan && (
                <ScenariosTab plan={execPlan.primaryPlan!} />
              )}
              {tab === 'scenarios' && !hasPlan && (
                <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
                  Scenarios are generated from the primary trade plan. No active plan.
                </div>
              )}
              {tab === 'risk' && hasPlan && (
                <RiskTab plan={execPlan.primaryPlan!} />
              )}
              {tab === 'risk' && !hasPlan && (
                <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
                  Risk analysis is generated from the primary trade plan. No active plan.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
